import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import {
  useQuoteChangeList,
  QUOTE_CHANGE_STATUS_LABELS,
  QUOTE_CHANGE_STATUSES,
  type QuoteChangeStatus,
} from '@/hooks/useQuoteContract';
import { canSeeQuoteContractMenu } from '@/lib/quoteContractPermissions';

const BADGE: Record<QuoteChangeStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  submitted: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-rose-100 text-rose-800',
  changes_requested: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-zinc-200 text-zinc-700',
};

function useSearchParam(name: string): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get(name) ?? '';
}

export default function QuoteChangeListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const canRead = canSeeQuoteContractMenu(permissions);
  const initialContractId = useSearchParam('contractId');
  const { rows, total, page, limit, loading, error, fetchChanges } =
    useQuoteChangeList();
  const [filterStatus, setFilterStatus] = useState<QuoteChangeStatus | ''>('');
  const [filterContract, setFilterContract] = useState(initialContractId);
  const [pageInput, setPageInput] = useState(1);

  useEffect(() => {
    if (!canRead) return;
    void fetchChanges({
      status: filterStatus || undefined,
      contractId: filterContract || undefined,
      page: pageInput,
      limit: 20,
    });
  }, [filterStatus, filterContract, pageInput, canRead, fetchChanges]);

  if (!canRead) return <AccessDenied resource="报价变更" />;

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-semibold">报价变更审批</h1>
        <p className="text-sm text-muted-foreground">
          已签署 / 已生效合同上发起的报价变更工单，审批通过后只调整合同生效金额。
        </p>
      </div>

      <Card className="space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="status">状态</Label>
            <select
              id="status"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus((e.target.value as QuoteChangeStatus | '') || '')
              }
            >
              <option value="">全部</option>
              {QUOTE_CHANGE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {QUOTE_CHANGE_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="contract">合同 ID</Label>
            <Input
              id="contract"
              value={filterContract}
              onChange={(e) => setFilterContract(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {error && (
        <Card className="bg-destructive/10 p-3 text-sm text-destructive">
          加载失败：{error.message}
        </Card>
      )}

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-3 py-2 text-left font-medium">变更号</th>
              <th className="px-3 py-2 text-left font-medium">标题</th>
              <th className="px-3 py-2 text-left font-medium">合同</th>
              <th className="px-3 py-2 text-left font-medium">原报价</th>
              <th className="px-3 py-2 text-left font-medium">状态</th>
              <th className="px-3 py-2 text-right font-medium">建议金额</th>
              <th className="px-3 py-2 text-left font-medium">更新时间</th>
              <th className="px-3 py-2 text-left font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">
                  加载中…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">
                  暂无变更
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-b hover:bg-muted/30">
                  <td className="px-3 py-2 font-mono text-xs">{row.changeNo}</td>
                  <td className="px-3 py-2 font-medium">{row.title}</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.contractId}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {row.originalQuoteId}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${BADGE[row.status]}`}>
                      {QUOTE_CHANGE_STATUS_LABELS[row.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {row.proposedTotalAmount}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {new Date(row.updatedAt).toLocaleString('zh-CN')}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      className="text-xs text-primary hover:underline"
                      onClick={() =>
                        navigate(`/quote-contract/change-requests/${row.id}`)
                      }
                    >
                      详情
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>共 {total} 条 · 第 {page} / {totalPages} 页</span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPageInput((p) => Math.max(1, p - 1))}
          >
            上一页
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPageInput((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </Button>
        </div>
      </div>
    </div>
  );
}
